import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Card, Col, Empty, Modal, Row, Select, Statistic, Table, Tag, Grid } from 'antd';
import { BarChartOutlined, BulbOutlined, WarningOutlined } from '@ant-design/icons';
import { metaAdsApi } from '../../utils/api';
import ColumnsCardList from '../common/ColumnsCardList';

/**
 * Formate un nombre monétaire sans décimales inutiles pour garder les indicateurs
 * publicitaires lisibles, quelle que soit la devise du compte publicitaire.
 *
 * @param {number|null|undefined} valeur - Montant à présenter
 * @param {string} devise - Code de devise de la campagne
 * @returns {string} Montant formaté ou tiret si la métrique est indisponible
 */
const formaterMontant = (valeur, devise = 'XOF') => {
  if (valeur === null || valeur === undefined) return '—';
  return `${Number(valeur).toLocaleString('fr-FR', { maximumFractionDigits: 0 })} ${devise}`;
};

/**
 * Affiche le tableau de bord consolidé des campagnes Meta sur la période choisie.
 * Les chiffres restent lus à la demande depuis Meta afin de ne pas présenter des
 * statistiques périmées comme si elles étaient actualisées.
 */
const MetaAdsDashboard = ({ aiOptions }) => {
  // Mobile (< md) : cartes à la place du tableau ; `=== false` évite un flash avant la mesure
  const screens = Grid.useBreakpoint();
  const isMobile = screens.md === false;
  const [period, setPeriod] = useState('last_30d');
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(false);
  const [recommendations, setRecommendations] = useState(null);
  const [recommendationsLoading, setRecommendationsLoading] = useState(false);
  const [candidateOrders, setCandidateOrders] = useState([]);
  const [selectedOrderIds, setSelectedOrderIds] = useState([]);

  /**
   * Charge les indicateurs, alertes et campagnes classées pour la période active.
   * L'appel synchronise également les statuts réels avant de calculer les totaux.
   */
  const chargerDashboard = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await metaAdsApi.getDashboard({ preset: period });
      setDashboard(data.data || null);
    } catch (error) {
      console.error('Erreur lors du chargement du tableau de bord Meta Ads:', error);
      setDashboard(null);
    } finally {
      setLoading(false);
    }
  }, [period]);

  /**
   * Recharge les statistiques lorsque l'utilisateur choisit une nouvelle période.
   */
  useEffect(() => {
    chargerDashboard();
  }, [chargerDashboard]);

  /**
   * Ouvre la préparation de l'analyse et charge les commandes de la même période.
   * L'utilisateur choisit ensuite manuellement les ventes liées à la campagne ;
   * aucune commande n'est attribuée automatiquement à Meta.
   */
  const chargerRecommandations = async (campaign) => {
    try {
      setRecommendationsLoading(true);
      setRecommendations({ campaign, data: null });
      setSelectedOrderIds([]);
      const { data } = await metaAdsApi.getCampaignRecommendationOrders(campaign.id, { preset: period });
      setCandidateOrders(data.data || []);
    } catch (error) {
      console.error('Erreur lors du chargement des commandes Meta Ads:', error);
      setCandidateOrders([]);
      setRecommendations({ campaign, error: error.response?.data?.message || 'Les commandes candidates sont indisponibles.' });
    } finally {
      setRecommendationsLoading(false);
    }
  };

  /**
   * Envoie à l'IA les seules commandes explicitement cochées par l'utilisateur.
   * Le serveur vérifie de nouveau leur propriétaire et n'envoie ni téléphone ni
   * adresse : les notes et statuts restent le contexte utile à la décision.
   */
  const analyserAvecCommandes = async () => {
    if (!recommendations?.campaign) return;
    try {
      setRecommendationsLoading(true);
      const { data } = await metaAdsApi.analyzeCampaignWithOrders(recommendations.campaign.id, {
        preset: period,
        ...aiOptions,
        selectedOrderIds
      });
      setRecommendations(previous => ({ ...previous, data: data.data, error: null }));
    } catch (error) {
      console.error('Erreur lors de l’analyse IA Meta Ads:', error);
      setRecommendations(previous => ({ ...previous, error: error.response?.data?.message || 'Analyse IA indisponible.' }));
    } finally {
      setRecommendationsLoading(false);
    }
  };

  const totals = dashboard?.totals || {};
  const currency = dashboard?.campaigns?.[0]?.currency || 'XOF';
  const colonnes = [
    {
      title: 'Campagne',
      dataIndex: 'name',
      key: 'name',
      render: (name, campagne) => (
        <div>
          <span>{name} <Tag>{campagne.status}</Tag></span>
          {campagne.products?.length > 0 && (
            <div className="text-xs text-gray-500">Produits : {campagne.products.join(', ')}</div>
          )}
        </div>
      )
    },
    {
      title: 'Dépense',
      dataIndex: 'spend',
      key: 'spend',
      render: (value) => formaterMontant(value, currency)
    },
    {
      title: 'CPA',
      dataIndex: 'costPerPurchase',
      key: 'costPerPurchase',
      render: (value) => formaterMontant(value, currency)
    },
    {
      title: 'Coût / conversation',
      dataIndex: 'costPerConversation',
      key: 'costPerConversation',
      render: (value) => formaterMontant(value, currency)
    },
    {
      title: 'ROAS',
      dataIndex: 'roas',
      key: 'roas',
      render: (value) => value ? `${Number(value).toFixed(2)}×` : '—'
    },
    {
      title: 'Analyse',
      key: 'analysis',
      render: (_, campaign) => <Button size="small" icon={<BulbOutlined />} onClick={() => chargerRecommandations(campaign)}>Conseils IA</Button>
    }
  ];

  return (
    <div className="space-y-6">
      <Card
        title={<span><BarChartOutlined className="text-blue-600 mr-2" />Vue d'ensemble des performances</span>}
        className="shadow-sm"
        loading={loading}
        extra={(
          <Select
            value={period}
            onChange={setPeriod}
            style={{ width: 190 }}
            options={[
              { value: 'today', label: "Aujourd'hui" },
              { value: 'last_7d', label: '7 derniers jours' },
              { value: 'last_30d', label: '30 derniers jours' },
              { value: 'maximum', label: 'Toute la période' }
            ]}
          />
        )}
      >
        <Row gutter={[16, 16]}>
          <Col xs={12} lg={6}><Statistic title="Dépense" value={totals.spend || 0} suffix={currency} /></Col>
          <Col xs={12} lg={6}><Statistic title="CPA" value={totals.costPerPurchase ?? '—'} suffix={totals.costPerPurchase !== null && totals.costPerPurchase !== undefined ? currency : ''} /></Col>
          <Col xs={12} lg={6}><Statistic title="Coût / conversation" value={totals.costPerConversation ?? '—'} suffix={totals.costPerConversation !== null && totals.costPerConversation !== undefined ? currency : ''} /></Col>
          <Col xs={12} lg={6}><Statistic title="ROAS" value={totals.roas ?? '—'} precision={totals.roas ? 2 : undefined} suffix={totals.roas ? '×' : ''} /></Col>
          <Col xs={12} lg={6}><Statistic title="CPM" value={totals.cpm ?? '—'} suffix={totals.cpm !== null && totals.cpm !== undefined ? currency : ''} /></Col>
          <Col xs={12} lg={6}><Statistic title="Fréquence" value={totals.frequency ?? '—'} precision={totals.frequency ? 2 : undefined} /></Col>
          <Col xs={12} lg={6}><Statistic title="Achats" value={totals.purchases || 0} /></Col>
          <Col xs={12} lg={6}><Statistic title="Conversations" value={totals.conversations || 0} /></Col>
        </Row>
      </Card>

      {dashboard?.alertes?.map((alerte) => (
        <Alert key={`${alerte.campaignId}-${alerte.message}`} showIcon icon={<WarningOutlined />} type={alerte.type} message={alerte.message} />
      ))}

      <Card title="Meilleures campagnes sur la période" className="shadow-sm">
        {isMobile ? (
          <ColumnsCardList
            columns={colonnes}
            dataSource={dashboard?.campaigns || []}
            headerKeys={['name']}
            actionKeys={['analysis']}
            clientPageSize={5}
            emptyText="Aucune performance disponible sur cette période"
          />
        ) : (
        <Table
          rowKey="id"
          dataSource={dashboard?.campaigns || []}
          columns={colonnes}
          pagination={{ pageSize: 5, hideOnSinglePage: true }}
          locale={{ emptyText: <Empty description="Aucune performance disponible sur cette période" /> }}
        />
        )}
      </Card>

      <Modal
        title={`Conseils IA — ${recommendations?.campaign?.name || ''}`}
        open={Boolean(recommendations)}
        onCancel={() => {
          setRecommendations(null);
          setCandidateOrders([]);
          setSelectedOrderIds([]);
        }}
        footer={null}
        width={900}
      >
        <Alert className="mb-4" type="info" showIcon message="Analyse consultative : aucune recommandation n'est appliquée automatiquement." />
        {recommendationsLoading && <p>Préparation de l’analyse…</p>}
        {recommendations?.error && <Alert type="error" message={recommendations.error} />}
        {!recommendations?.data && !recommendationsLoading && !recommendations?.error && (
          <div className="space-y-3">
            <Alert
              type="warning"
              showIcon
              message="Attribuez manuellement les commandes liées à cette campagne"
              description="Les commandes sélectionnées, leurs statuts et leurs notes seront analysés. Les coordonnées des clients ne sont jamais envoyées à l’IA."
            />
            <Table
              rowKey="id"
              size="small"
              pagination={{ pageSize: 5, hideOnSinglePage: true }}
              dataSource={candidateOrders}
              rowSelection={{
                selectedRowKeys: selectedOrderIds,
                onChange: (ids) => setSelectedOrderIds(ids)
              }}
              locale={{ emptyText: 'Aucune commande créée pendant cette période.' }}
              columns={[
                { title: 'Commande', dataIndex: 'order_number', render: (value) => `#${value}` },
                { title: 'Statut', dataIndex: 'status', render: (value) => <Tag>{value || 'inconnu'}</Tag> },
                { title: 'Montant', dataIndex: 'total_amount', render: (value) => formaterMontant(value, 'XOF') },
                { title: 'Source', dataIndex: 'source', render: (value) => value || '—' },
                {
                  title: 'Notes / motif',
                  key: 'notes',
                  render: (_, order) => order.delivery_notes || order.notes || '—'
                }
              ]}
            />
            <Button type="primary" loading={recommendationsLoading} onClick={analyserAvecCommandes}>
              Analyser {selectedOrderIds.length > 0 ? `${selectedOrderIds.length} commande${selectedOrderIds.length > 1 ? 's' : ''} sélectionnée${selectedOrderIds.length > 1 ? 's' : ''}` : 'sans commande attribuée'}
            </Button>
          </div>
        )}
        {recommendations?.data && (
          <div className="space-y-3">
            <p>{recommendations.data.summary}</p>
            {recommendations.data.recommendations.map((item, index) => (
              <Card key={index} size="small">
                <Tag color={item.action === 'pause' ? 'red' : item.action === 'increase' ? 'green' : 'blue'}>
                  {{ increase: 'Augmenter', decrease: 'Réduire', pause: 'Mettre en pause', keep: 'Conserver' }[item.action]}
                </Tag>
                <Tag>{item.confidence === 'high' ? 'Confiance élevée' : item.confidence === 'medium' ? 'Confiance moyenne' : 'Confiance faible'}</Tag>
                <p className="mt-2 mb-0">{item.reason}</p>
              </Card>
            ))}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default MetaAdsDashboard;
